import { motion, AnimatePresence } from 'framer-motion'
import { Sparkles, Gem, Heart, Star } from 'lucide-react'
import { Button, Modal } from '@/components/ui'
import { CharacterCelebration } from '@/components/character'
import { useSettingsStore } from '@/stores/useSettingsStore'
import {
  MILESTONE_REACTIONS,
  type MilestonePercent,
  MILESTONES,
} from '@/lib/milestones'
import type { CharacterId } from '@/types'

interface MilestoneCelebrationProps {
  isOpen: boolean
  onClose: () => void
  milestone: MilestonePercent
  characterId: CharacterId
  gemReward: number
  affectionBonus: number
  newLevel?: number
}

export function MilestoneCelebration({
  isOpen,
  onClose,
  milestone,
  characterId,
  gemReward,
  affectionBonus,
  newLevel,
}: MilestoneCelebrationProps) {
  const { contentLevel } = useSettingsStore()

  const milestoneData = MILESTONES[milestone]
  const reaction = MILESTONE_REACTIONS[characterId][milestoneData.type]

  const isLevelUp = milestone === 1.0
  const randomDialogue = reaction.dialogue[Math.floor(Math.random() * reaction.dialogue.length)]

  // Get title based on milestone
  const getTitle = () => {
    switch (milestone) {
      case 0.25: return '25% Milestone!'
      case 0.5: return 'Halfway There!'
      case 0.75: return '75% - Almost!'
      case 1.0: return `Level ${newLevel}!`
      default: return 'Milestone!'
    }
  }

  // Get emoji rain based on milestone
  const getEmojis = () => {
    if (isLevelUp) return ['⭐', '🌟', '✨', '💫', '🎉']
    if (milestone >= 0.75) return ['💕', '✨', '🔥']
    if (milestone >= 0.5) return ['💖', '✨']
    return ['💗', '✨']
  }

  const emojis = getEmojis()

  return (
    <AnimatePresence>
      {isOpen && (
        <Modal isOpen={isOpen} onClose={onClose} showCloseButton={false} size="sm">
          <div className="text-center py-2 relative overflow-hidden">
            {/* Floating emojis background */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
              {[...Array(isLevelUp ? 20 : 10)].map((_, i) => (
                <motion.span
                  key={i}
                  initial={{
                    opacity: 0,
                    y: 100,
                    x: Math.random() * 200 - 100,
                    scale: 0.5,
                  }}
                  animate={{
                    opacity: [0, 1, 0],
                    y: -200,
                    x: Math.random() * 200 - 100,
                    scale: [0.5, 1, 0.5],
                    rotate: Math.random() * 360,
                  }}
                  transition={{
                    duration: 2 + Math.random() * 2,
                    delay: Math.random() * 0.5,
                    repeat: Infinity,
                    repeatDelay: Math.random() * 2,
                  }}
                  className="absolute text-2xl"
                  style={{
                    left: `${Math.random() * 100}%`,
                    bottom: 0,
                  }}
                >
                  {emojis[Math.floor(Math.random() * emojis.length)]}
                </motion.span>
              ))}
            </div>

            {/* Title */}
            <motion.div
              initial={{ scale: 0, rotate: -10 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 15 }}
              className="relative z-10 mb-4"
            >
              {isLevelUp ? (
                <div className="flex items-center justify-center gap-2">
                  <Star className="text-accent fill-accent" size={28} />
                  <h2 className="font-display font-bold text-3xl text-gradient">
                    {getTitle()}
                  </h2>
                  <Star className="text-accent fill-accent" size={28} />
                </div>
              ) : (
                <h2 className="font-display font-bold text-2xl text-gradient flex items-center justify-center gap-2">
                  <Sparkles className="text-primary" size={24} />
                  {getTitle()}
                  <Sparkles className="text-primary" size={24} />
                </h2>
              )}
            </motion.div>

            {/* Character display with animation */}
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="relative z-10 mb-4 flex justify-center"
            >
              <CharacterCelebration
                characterId={characterId}
                contentLevel={contentLevel}
                size="md"
                particleType={isLevelUp ? 'confetti' : 'hearts'}
              />
            </motion.div>

            {/* Dialogue */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-lg text-text-secondary mb-4 relative z-10 italic"
            >
              "{randomDialogue}"
            </motion.p>

            {/* Rewards */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="flex justify-center gap-6 mb-6 relative z-10"
            >
              {gemReward > 0 && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.5, type: 'spring' }}
                  className="flex items-center gap-2 bg-gem/20 px-4 py-2 rounded-xl"
                >
                  <Gem className="text-gem" size={20} />
                  <span className="font-bold text-gem">+{gemReward}</span>
                </motion.div>
              )}
              {affectionBonus > 0 && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.6, type: 'spring' }}
                  className="flex items-center gap-2 bg-red-400/20 px-4 py-2 rounded-xl"
                >
                  <Heart className="text-red-400 fill-red-400" size={20} />
                  <span className="font-bold text-red-400">+{affectionBonus}</span>
                </motion.div>
              )}
            </motion.div>

            {/* Special level up bonus text */}
            {isLevelUp && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7 }}
                className="text-sm text-accent mb-4 relative z-10"
              >
                ✨ New content may be unlocked! Check the Closet! ✨
              </motion.p>
            )}

            {/* Continue button */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
              className="relative z-10"
            >
              <Button onClick={onClose} size="lg">
                {isLevelUp ? 'Amazing! 🎉' : 'Continue ▶'}
              </Button>
            </motion.div>
          </div>
        </Modal>
      )}
    </AnimatePresence>
  )
}
